import React from 'react';
// Modules
import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { NextPage } from 'next/types';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
// Components
import Page from '../components/layout/Page';
// Contexts
import { useAuth } from '../contexts/AuthContext';
// Utils
import { DEFAULT_ERROR_MESSAGE } from '../utils/constants';
import { parseError } from '../utils/helpers';

interface FormData {
  email: string;
  message: string;
  name: string;
  phoneNumber: string;
}

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email('Alamat email tidak valid.')
    .required('Alamat email tidak boleh kosong.'),
  message: yup.string().required('Pesan tidak boleh kosong.'),
  name: yup.string().required('Nama tidak boleh kosong.'),
});

const ContactPage: NextPage = () => {
  const toast = useToast();

  const { currentUser } = useAuth();

  const {
    control, errors, formState, handleSubmit, reset, setValue,
  } = useForm<FormData>({
    defaultValues: {
      email: '',
      message: '',
      name: '',
      phoneNumber: '',
    },
    resolver: yupResolver(validationSchema),
  });

  React.useEffect(() => {
    setValue('email', currentUser?.email ?? '');
    setValue('name', currentUser?.displayName ?? '');
    setValue('phoneNumber', currentUser?.phoneNumber ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await axios.post('/api/send-email', data);

      if (result.status !== 200) {
        throw new Error(DEFAULT_ERROR_MESSAGE);
      }

      reset();

      toast({
        description: 'Pesan telah berhasil terkirim.',
        status: 'success',
      });
    } catch (e) {
      toast({
        description: parseError(e),
        status: 'error',
      });
    }
  });

  return (
    <Page>
      <Container py="16">
        <form onSubmit={onSubmit}>
          <VStack alignItems="stretch" spacing="8">
            <VStack alignItems="stretch">
              <Heading as="h1" size="lg">
                Kontak Kami
              </Heading>
              <Box bgColor="primary.500" h="4px" w="64px" />
            </VStack>
            <VStack alignItems="stretch">
              <Controller
                control={control}
                name="name"
                render={({ onBlur, onChange, value }) => (
                  <FormControl id="name" isInvalid={!!errors.name}>
                    <FormLabel>Nama</FormLabel>
                    <Input
                      onBlur={onBlur}
                      onChange={(e) => onChange(e.target.value)}
                      value={value}
                    />
                    {errors.name && (
                      <FormHelperText color="red.500">{errors.name.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="email"
                render={({ onBlur, onChange, value }) => (
                  <FormControl id="email" isInvalid={!!errors.email}>
                    <FormLabel>Alamat email</FormLabel>
                    <Input
                      onBlur={onBlur}
                      onChange={(e) => onChange(e.target.value)}
                      value={value}
                    />
                    {errors.email && (
                      <FormHelperText color="red.500">{errors.email.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="phoneNumber"
                render={({ onBlur, onChange, value }) => (
                  <FormControl id="phone">
                    <FormLabel>Nomor HP</FormLabel>
                    <Input
                      onBlur={onBlur}
                      onChange={(e) => onChange(e.target.value)}
                      type="tel"
                      value={value}
                    />
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="message"
                render={({ onBlur, onChange, value }) => (
                  <FormControl id="message" isInvalid={!!errors.message}>
                    <FormLabel>Pesan</FormLabel>
                    <Textarea
                      onBlur={onBlur}
                      onChange={(e) => onChange(e.target.value)}
                      value={value}
                    />
                    {errors.message && (
                      <FormHelperText color="red.500">{errors.message.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </VStack>
            <Button
              alignSelf="flex-start"
              colorScheme="primary"
              isLoading={formState.isSubmitting}
              textColor="black"
              type="submit"
            >
              Kirim
            </Button>
            <Divider />
            <SimpleGrid columns={[0, 2]} spacing="8">
              <VStack alignItems="flex-start">
                <Text fontSize="lg" fontWeight="bold">
                  Alamat
                </Text>
                <Text
                  as="a"
                  href="https://goo.gl/maps/K5a599qUSWAScK3P7"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Ruko Panglima Sudirman B-2
                  <br />
                  Jalan Hasanudin No. 29
                  <br />
                  Tulungagung, Jawa Timur
                </Text>
              </VStack>
              <VStack alignItems="flex-start">
                <Text fontSize="lg" fontWeight="bold">
                  Jam Buka
                </Text>
                <Text>Senin-Sabtu: 07.30-20.00</Text>
              </VStack>
              <VStack alignItems="flex-start">
                <Text fontSize="lg" fontWeight="bold">
                  Kontak
                </Text>
                <Text
                  as="a"
                  href="https://api.whatsapp.com/send?phone=6282139545130"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  0821-3954-5130
                </Text>
              </VStack>
            </SimpleGrid>
          </VStack>
        </form>
      </Container>
    </Page>
  );
};

export default ContactPage;
